<template>
  <div>
    <div v-if="message" id="email_body" :class="`${css}`">
      <div
        v-if="isLoading"
        class="w-full flex justify-center items-center pt-1 pb-4"
      >
        <CustomLoader :size="28" />
      </div>
      <div v-show="!isLoading">
        <iframe
          ref="mainContentIframe"
          sandbox="allow-scripts allow-same-origin"
          class="w-full border-none rounded-lg bg-transparent text_color m-0 p-0 add_scroll_bar"
        ></iframe>

        <iframe
          v-if="hasQuotedContent"
          ref="quotedContentIframe"
          sandbox="allow-scripts allow-same-origin"
          class="w-full border-none rounded-lg bg-transparent text_color m-0 p-0 add_scroll_bar"
          :style="{
            display: showQuotedContent ? 'block' : 'none',
          }"
        ></iframe>
      </div>

      <button
        v-if="hasQuotedContent"
        @click="toggleQuotedContent"
        class="text-sm secondary_color mt-2 pb-4"
      >
        <span class="show_quoted">
          <div class="flex flex-row">
            <ThemeImage
              class="w-1.5"
              :icon="imageNames.MORE_DOTS"
              alt="more_dots"
            />
            <ThemeImage
              class="w-1.5"
              :icon="imageNames.MORE_DOTS"
              alt="more_dots"
            />
            <ThemeImage
              class="w-1.5"
              :icon="imageNames.MORE_DOTS"
              alt="more_dots"
            />
          </div>
        </span>
      </button>
    </div>
    <div
      v-if="emailAction === emailDirections.SENT"
      class="flex flex-col gap-4 mt-4"
    >
      <Engagement :value="emailMetrics" />
      <div v-if="sentVia" class="flex gap-1">
        <ThemeImage :icon="imageNames.GO_TO_LINK_GREY" class="size-[14px]" />
        <span class="secondary_color text-xs font-normal"
          >Sent via {{ sentVia?.type || "" }}
          <span class="text-xs secondary_color font-semibold">{{
            sentVia?.name || ""
          }}</span></span
        >
      </div>
    </div>
    <div
      v-if="attachments.length > 0"
      class="flex flex-row flex-wrap gap-1 mt-4 mb-1"
    >
      <AttachedItem
        v-for="(file, index) in attachments"
        :file="file"
        :key="`${index}-${file.id}`"
        :downloadable="true"
        :download-api="uploadDownloadAPITypes.CONVERSATION_ATTACHMENTS"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, computed } from "vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import AttachedItem from "@/src/components/attachment_components/AttachedItem.vue";
import { convertURLsToLinks, removeTrackingPixels, stripInlineBackgroundStyles } from "@/src/utils/utils.js";
import { uploadDownloadAPITypes } from "@/src/constants/commonConstants.js";

import store from "@/src/store";
import { imageNames } from "@/src/constants/imageConstants.js";
import Engagement from "@/src/components/emails/renderers/Engagement.vue";
import { emailDirections } from "@/src/constants/emailConstants.js";
import { fontFaceDeclarations } from "@/src/constants/styleConstants.js";

const props = defineProps({
  message: { type: String, required: true },
  attachments: { type: Array, default: [] },
  css: { type: String, default: "mt-3" },
  emailAction: { type: String, default: null },
  emailMetrics: { type: Object, default: () => ({}) },
  sentVia: { type: Object, default: null },
});

const mainContentIframe = ref(null);
const quotedContentIframe = ref(null);
const isLoading = ref(true);
const hasQuotedContent = ref(false);
const showQuotedContent = ref(false);

const effectiveTheme = computed(() => store.getters["theme/getEffectiveTheme"]);

// Updated style injection with more email-friendly styles
const injectStyles = (iframeDoc) => {
  if (!iframeDoc) {
    return;
  }

  if (!iframeDoc.head) {
    return;
  }

  const rootStyles = getComputedStyle(document.documentElement);
  const style = document.createElement("style");

  const styleContent = `
    ${fontFaceDeclarations}

    :root {
      --text-color: ${rootStyles.getPropertyValue("--text-color")};
      --email-iframe-text-color: ${rootStyles.getPropertyValue("--email-iframe-text-color")};
    }

    *::-webkit-scrollbar {
      display: none;
    }

    * {
      -ms-overflow-style: none; /* Internet Explorer 10+ */
      scrollbar-width: none; /* Firefox */
    }

    html {
      background: transparent !important;
    }
    body {
      font-family: "Proxima Nova", sans-serif;
      background: transparent !important;
      color: var(--text-color) !important;
      border-radius: 8px;
      overflow-y: visible !important;
      padding: 0px;
      margin: 0px;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 17px;
    }
    main {
      background: transparent;
      color: var(--text-color) !important;
      border-radius: 2px;
    }
    p, span, b, td p, td span,div, font, li {
      color: var(--email-iframe-text-color) !important;
    }
    strong, h1, h2, h3, h4 {
      color: var(--text-color) !important;
    }
    a, a:visited {
      color: #a05bd5 !important;
      text-decoration: underline;
    }
    table.body {
      background: transparent;
    }
    div, table, td {
      background-color: transparent !important;
    }
    table {
      color: var(--text-color) !important;
    }
    .u-row {
      display: table !important;
      width: 100% !important;
    }
    .u-col {
      display: table-cell !important;
    }
    img {
      max-width: 100% !important;
      height: auto !important;
      display: inline-block !important;
    }
    #backgroundTable {
      background-color: transparent !important;
    }
    #divRplyFwdMsg {
      opacity: 0.8;
      font-size: 0.9em;
    }
    hr {
      border-color: var(--white-opacity-20) !important;
      margin: 10px 0;
    }
    button {
      background-color: #141414 !important;
      color: var(--text-color) !important;
    }
  `;

  try {
    const existingStyle = iframeDoc.head.querySelector("style");
    if (existingStyle) {
      existingStyle.remove();
    }

    style.textContent = styleContent;
    iframeDoc.head.appendChild(style);

    setTimeout(() => {
      try {
        const bodyStyles = window.getComputedStyle(iframeDoc.body);
        const tables = iframeDoc.querySelectorAll("table");
        if (tables.length > 0) {
          const tableStyle = window.getComputedStyle(tables[0]);
        }

        // Check image styles if there are images
        const images = iframeDoc.querySelectorAll("img");
      } catch (e) {}
    }, 100);
  } catch (e) {}
};

const updateIframeStyles = () => {
  if (mainContentIframe.value) {
    const mainDoc =
      mainContentIframe.value.contentDocument ||
      mainContentIframe.value.contentWindow?.document;
    if (mainDoc) {
      injectStyles(mainDoc);
    }
  }

  if (quotedContentIframe.value && showQuotedContent.value) {
    const quotedDoc =
      quotedContentIframe.value.contentDocument ||
      quotedContentIframe.value.contentWindow?.document;
    if (quotedDoc) {
      injectStyles(quotedDoc);
    }
  }
};

const updateIframeHeight = (iframe) => {
  if (!iframe) {
    return;
  }
  setTimeout(() => {
    const iframeDoc =
      iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!iframeDoc) {
      return;
    }

    const body = iframeDoc.body;
    const bodyScrollHeight = body.scrollHeight;
    const bodyOffsetHeight = body.offsetHeight;
    const buffer = 40;
    const height = Math.max(bodyScrollHeight, bodyOffsetHeight) + buffer;

    iframe.style.height = `${height}px`;
  }, 100);
};

const observeIframeContent = (iframe) => {
  if (!iframe) {
    return null;
  }

  try {
    if (!iframe.contentWindow) {
      return null;
    }

    const iframeDoc =
      iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!iframeDoc || !iframeDoc?.body) {
      return null;
    }

    const observer = new MutationObserver((mutations) => {
      updateIframeHeight(iframe);
    });

    observer.observe(iframeDoc.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    return observer;
  } catch (e) {
    return null;
  }
};

const toggleQuotedContent = () => {
  showQuotedContent.value = !showQuotedContent.value;

  if (quotedContentIframe.value) {
    quotedContentIframe.value.style.display = showQuotedContent.value
      ? "block"
      : "none";

    if (showQuotedContent.value) {
      nextTick(() => {
        const quotedDoc =
          quotedContentIframe.value.contentDocument ||
          quotedContentIframe.value.contentWindow?.document;
        if (quotedDoc) {
          injectStyles(quotedDoc);
        }
        updateIframeHeight(quotedContentIframe.value);
      });
    }
  } else {
  }
};

const loadIframeContent = (content) => {
  if (!mainContentIframe.value || !content) {
    isLoading.value = false;
    return;
  }

  let filteredContent = removeTrackingPixels(content);
  filteredContent = stripInlineBackgroundStyles(filteredContent);

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = filteredContent;

  const hasHTMLStructure =
    filteredContent.includes("<div") ||
    filteredContent.includes("<table") ||
    filteredContent.includes("<p") ||
    filteredContent.includes("<html");

  if (!hasHTMLStructure) {
    filteredContent = filteredContent.replace(/\r\n/g, "<br>");
    filteredContent = filteredContent.replace(/\n/g, "<br>");
  }

  const gmailQuoteContainer = tempDiv.querySelector(".gmail_quote_container");
  const gmailQuote = tempDiv.querySelector(".gmail_quote");
  const outlookQuote = tempDiv.querySelector(
    'hr[style*="display:inline-block;width:98%"]'
  );

  if (gmailQuoteContainer) {
    const mainContentHTML = filteredContent.substring(
      0,
      filteredContent.indexOf('<div class="gmail_quote gmail_quote_container">')
    );
    const quotedContentHTML = filteredContent.substring(
      filteredContent.indexOf('<div class="gmail_quote gmail_quote_container">')
    );

    loadMainContent(mainContentHTML);
    hasQuotedContent.value = true;
    nextTick(() => {
      loadQuotedContent(quotedContentHTML);
    });
  } else if (gmailQuote && !gmailQuoteContainer) {
    const mainContentHTML = filteredContent.substring(
      0,
      filteredContent.indexOf('class="gmail_quote"')
    );
    const quotedContentHTML = filteredContent.substring(
      filteredContent.indexOf('class="gmail_quote"')
    );

    loadMainContent(mainContentHTML);
    hasQuotedContent.value = true;
    nextTick(() => {
      loadQuotedContent(quotedContentHTML);
    });
  } else if (outlookQuote) {
    const outlookQuoteIndex = filteredContent.indexOf(
      'hr style="display:inline-block;width:98%"'
    );
    const mainContent = filteredContent.substring(0, outlookQuoteIndex);
    const quotedContent = filteredContent.substring(outlookQuoteIndex);

    loadMainContent(mainContent);
    hasQuotedContent.value = true;
    nextTick(() => {
      loadQuotedContent(quotedContent);
    });
  } else {
    loadMainContent(filteredContent);
    hasQuotedContent.value = false;
  }

  isLoading.value = false;
};

const loadMainContent = (content) => {
  const updatedContent = convertURLsToLinks(content);
  const iframe = mainContentIframe.value;
  if (!iframe) {
    return;
  }
  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
  iframeDoc.open();
  const htmlToWrite = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body>
      ${updatedContent}
    </body>
    </html>
  `;

  iframeDoc.write(htmlToWrite);
  iframeDoc.close();

  nextTick(() => {
    injectStyles(iframeDoc);
    addLinkClickHandler(iframeDoc);
    updateIframeHeight(iframe);
    observeIframeContent(iframe);
  });
};

const loadQuotedContent = (content) => {
  const updatedContent = convertURLsToLinks(content);

  const iframe = quotedContentIframe.value;
  if (!iframe) {
    return;
  }

  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

  iframeDoc.open();
  const htmlToWrite = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body>
      ${updatedContent}
    </body>
    </html>
  `;
  iframeDoc.write(htmlToWrite);
  iframeDoc.close();

  nextTick(() => {
    injectStyles(iframeDoc);
    addLinkClickHandler(iframeDoc);
    updateIframeHeight(iframe);
    observeIframeContent(iframe);
  });
};

const addLinkClickHandler = (iframeDoc) => {
  if (!iframeDoc || !iframeDoc.body) {
    return;
  }

  try {
    iframeDoc.body.addEventListener("click", (event) => {
      const link = event.target.closest("a");
      if (link) {
        event.preventDefault();
        window.open(link.href, "_blank");
      }
    });
    const links = iframeDoc.querySelectorAll("a");
  } catch (e) {}
};

watch(effectiveTheme, () => {
  nextTick(() => {
    updateIframeStyles();
  });
});

watch(
  () => props.message,
  (newMessage) => {
    if (newMessage) {
      isLoading.value = true;
      nextTick(() => {
        loadIframeContent(newMessage);
      });
    } else {
      isLoading.value = false;
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (props.message) {
    loadIframeContent(props.message);
  }

  updateIframeStyles();
});
</script>

<style scoped>
.show_quoted {
  height: 12px;
  padding: 2px;
  cursor: pointer;
  border-radius: 24px;
  background: var(--white-opacity-8);
  transition: background 0.2s ease-in-out;
  display: flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
}

.show_quoted:hover {
  background: var(--white-opacity-16);
}

.add_scroll_bar {
  transition: height 0.3s ease;
}
</style>
